import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { TarifReducedDto } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import { UpdateTarifFormData, UpdateTarifSchema } from "./tarifForm";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetTarifs, useUpdateTarif } from "@/api/tarif.api";
import { Form } from "@/components/ui/form";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Pencil } from "lucide-react";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";

interface TarifUpdateModalProps {
  open: boolean;
  closeModal: () => void;
  tarif: TarifReducedDto;
}

export const TarifUpdateModal = ({ open, closeModal, tarif }: TarifUpdateModalProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { mutateAsync: updateTarif, isPending } = useUpdateTarif();
  const { data: tarifs } = useGetTarifs();
  const [allowEditFlags, setAllowEditFlags] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const form = useForm<UpdateTarifFormData>({
    resolver: zodResolver(UpdateTarifSchema),
    defaultValues: {
      label: tarif.label,
      tarifIdToCopy: tarif.followingId || tarif.copiedTarifId,
      shouldFollowTarif: tarif.followingId !== null,
      changePercentage: tarif.changePercentage,
    },
  });

  const onSubmit = async (data: UpdateTarifFormData) => {
    const tarifIdToCopyChanged = data.tarifIdToCopy !== tarif.copiedTarifId;
    const shouldFollowTarifChanged = data.shouldFollowTarif !== !!tarif.followingId;
    const changePercentageChanged = data.changePercentage !== tarif.changePercentage;

    if (tarifIdToCopyChanged || shouldFollowTarifChanged || changePercentageChanged) {
      // Show confirmation modal for critical changes
      setShowConfirmation(true);
      return;
    }

    // No critical changes, proceed with update
    await performUpdate(data);
  };

  const performUpdate = async (data: UpdateTarifFormData) => {
    await updateTarif({
      id: tarif.id,
      payload: {
        label: data.label,
        ...(data.changePercentage !== tarif.changePercentage && {
          changePercentage: data.changePercentage,
        }),
        ...((data.tarifIdToCopy !== tarif.copiedTarifId || data.shouldFollowTarif) && {
          tarifIdToCopy: data.tarifIdToCopy,
        }),
        ...(data.shouldFollowTarif !== !!tarif.followingId && {
          shouldFollowTarif: data.shouldFollowTarif,
        }),
      },
    });
    closeModal();
  };

  const handleConfirmUpdate = async () => {
    const currentData = form.getValues();
    await performUpdate(currentData);
    setShowConfirmation(false);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={closeModal}
        title={t("tarifUpdateModal.title", { label: tarif.label })}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button type="submit" form="update-tarif-form" disabled={isPending} className="w-full">
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("tarifUpdateModal.submit")
              )}
            </Button>
            <Button variant="outline" onClick={closeModal} className="w-full">
              {t("common:actions.cancel")}
            </Button>
          </div>
        }
      >
        <Form {...form}>
          <form
            id="update-tarif-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-2"
          >
            <ControlledUnifiedInput
              name="label"
              control={form.control}
              label={t("tarifUpdateModal.labelLabel")}
              placeholder={t("tarifUpdateModal.labelPlaceholder")}
            />
            {!tarif.isDefault && !tarif.isExpress && (
              <>
                <div className="flex w-full flex-row items-center gap-2">
                  <ControlledSelectV2
                    name="tarifIdToCopy"
                    control={form.control}
                    placeholder={t("tarifUpdateModal.tarifToCopyPlaceholder")}
                    label={t("tarifUpdateModal.tarifToCopyLabel")}
                    options={
                      tarifs?.data
                        ?.filter(
                          (e) =>
                            e.id !== tarif.id && e.followingId === null && e.isExpress === false,
                        )
                        .map((tarif) => ({
                          label: tarif.label,
                          value: tarif.id,
                        })) ?? []
                    }
                    wrapperClassName="w-full"
                    isDisabled={tarif.isDefault || tarif.isExpress || !(allowEditFlags & 1)}
                    clearable={true}
                  />
                  {!(allowEditFlags & 1) && (
                    <Button
                      variant="outline"
                      type="button"
                      size="icon"
                      onClick={() => setAllowEditFlags(allowEditFlags ^ 1)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="flex w-full flex-row items-center gap-2">
                  <ControlledUnifiedInput
                    name="changePercentage"
                    control={form.control}
                    label={t("tarifUpdateModal.changePercentageLabel")}
                    placeholder={t("tarifUpdateModal.changePercentagePlaceholder")}
                    wrapperClassName="w-full"
                    inputType="integer"
                    disabled={!form.watch("tarifIdToCopy") || !(allowEditFlags & 2)}
                  />
                  {!(allowEditFlags & 2) && (
                    <Button
                      variant="outline"
                      type="button"
                      size="icon"
                      onClick={() => setAllowEditFlags(allowEditFlags ^ 2)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-row items-center justify-between gap-2 pb-2">
                  <ControlledCheckbox
                    name="shouldFollowTarif"
                    control={form.control}
                    label={t("tarifUpdateModal.followTarifLabel")}
                    disabled={tarif.isDefault || tarif.isExpress || !(allowEditFlags & 4)}
                  />
                  {!(allowEditFlags & 4) && (
                    <Button
                      variant="outline"
                      type="button"
                      size="icon"
                      onClick={() => setAllowEditFlags(allowEditFlags ^ 4)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </form>
        </Form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmation}
        onClose={handleCancelConfirmation}
        title={t("tarifUpdateModal.confirmTitle")}
        size="sm"
        footer={
          <>
            <Button onClick={handleConfirmUpdate} disabled={isPending} className="w-full">
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common:actions.confirm")
              )}
            </Button>
            <Button variant="ghost" onClick={handleCancelConfirmation} className="w-full">
              {t("common:actions.cancel")}
            </Button>
          </>
        }
      >
        <div className="py-4">
          <p className="mb-4 text-sm text-gray-700">{t("tarifUpdateModal.confirmDescription")}</p>
          <p className="text-sm text-gray-600">{t("tarifUpdateModal.confirmQuestion")}</p>
        </div>
      </Modal>
    </>
  );
};
